import {
  Add,
  GroupsOutlined,
  ManageAccounts,
  PeopleAltOutlined,
  PrecisionManufacturingOutlined,
} from "@mui/icons-material";
import { Box, Chip, Paper, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useContext, useEffect, useRef, useState } from "react";
import { organizationsApi } from "api";
import { ChoosePanel } from "../components";
import { useIsCompactLandscape, useOrganizations, useSelectedOrganization, useTranslations } from "hooks";
import { User } from "components/UserItem";
import { OrganizationsContext } from "contexts";
import { Organization } from "model/organizationModel";
import OrganizationsHeader from "./organizations/OrganizationsHeader";
import useOrganizationActions from "./organizations/useOrganizationActions";
import { getTranslation, OutletType } from "common";
import { useOutletContext } from "react-router-dom";
import SearchBar from "components/SearchBar";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

const ORGANIZATION_UNITS = {
  USERS: 1,
  GROUPS: 2,
  ROLES: 3,
  ROBOTS: 4,
} as const;

export interface OrganizationCounts {
  users_count: number;
  groups_count: number;
  roles_count: number;
  robots_count: number;
}

const Organizations2 = () => {
  const {
    actions: { setSelectedOrganization },
  } = useContext(OrganizationsContext);
  const [organizations, refreshOrganizations] = useOrganizations(1);
  const selectedOrganization = useSelectedOrganization();
  const administrator = Boolean(selectedOrganization?.administrator);
  const getUserUntilPageRef = useRef<
    ((userSelected: User) => Promise<void>) | undefined
  >(undefined);
  const translations = useTranslations();
  const getOrganizations = useRef<Function | undefined>(refreshOrganizations);
  const getInvitedUsers = useRef<Function | undefined>();
  const getGroups = useRef<Function | undefined>();
  const getUsers = useRef<Function | undefined>();
  const getRoles = useRef<Function | undefined>();
  const getRobots = useRef<Function | undefined>();
  const [userSelected, setUserSelected] = useState<User | null>(null);
  const [unit, setUnit] = useState<number>(ORGANIZATION_UNITS.USERS);
  const [counts, setCounts] = useState<OrganizationCounts | null>(null);
  const { setSecondFilter } = useOutletContext<OutletType>();
  const isCompactLandscape = useIsCompactLandscape();

  useEffect(() => {
    getOrganizations.current = refreshOrganizations;
  }, [refreshOrganizations]);

  useEffect(() => {
    if (!selectedOrganization?.id && organizations[0]) {
      setSelectedOrganization(organizations[0]);
    }
  }, [organizations, selectedOrganization?.id, setSelectedOrganization]);

  useEffect(() => {
    const getOrganizationCounts = async () => {
      if (!selectedOrganization?.id) return;

      try {
        const { data } = await organizationsApi.get(
          `${selectedOrganization.id}/counts`
        );
        setCounts(data);
      } catch {
        setCounts(null);
      }
    };

    setCounts(null);
    getOrganizationCounts();
  }, [selectedOrganization?.id]);

  const updateSelectedOrganization = (organization: Organization) => {
    setSelectedOrganization(organization);
    setUserSelected(null);
  };

  const actions = useOrganizationActions({
    organizationId: selectedOrganization?.id,
    selectedOrganization,
    getOrganizations,
    getGroups,
    getInvitedUsers,
    getRoles,
    getRobots,
  });

  const primaryAction = (() => {
    if (unit === ORGANIZATION_UNITS.GROUPS) {
      return {
        id: "btn-groups-card",
        label: getTranslation(translations, "groups.button.create"),
        onClick: actions.openCreateGroup,
      };
    }
    if (unit === ORGANIZATION_UNITS.ROLES) {
      return {
        id: "btn-roles-card",
        label: getTranslation(translations, "roles.button.create"),
        onClick: actions.openCreateRole,
      };
    }
    if (unit === ORGANIZATION_UNITS.ROBOTS) {
      return {
        id: "btn-robots-shop",
        label: getTranslation(translations, "robots.name"),
        onClick: actions.openRobotsShop,
      };
    }
    return {
      id: "btn-invite-card",
      label: getTranslation(translations, "users.button.inviteuser"),
      onClick: actions.openInviteUser,
    };
  })();

  const renderTabLabel = (label: string, count?: number) => (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      {!isCompactLandscape && (
        <Box component="span" sx={{ fontWeight: "bold" }}>
          {label}
        </Box>
      )}
      {!!count && (
        <Chip
          size="small"
          label={count}
          sx={(theme) => ({
            height: 20,
            fontSize: 11,
            fontWeight: 700,
            bgcolor: alpha(
              theme.palette.primary.main,
              theme.palette.mode === "dark" ? 0.22 : 0.12
            ),
            border: "1px solid",
            borderColor: alpha(
              theme.palette.primary.main,
              theme.palette.mode === "dark" ? 0.7 : 0.35
            ),
            color:
              theme.palette.mode === "dark"
                ? theme.palette.primary.light
                : theme.palette.primary.dark,
            "& .MuiChip-label": {
              px: 0.75,
            },
          })}
        />
      )}
    </Stack>
  );

  return (
    <React.Fragment>
      {actions.dialogs}
      <Stack sx={{ height: "100%", overflow: "hidden", bgcolor: "background.default" }} spacing={2}>
        <OrganizationsHeader
          organizations={organizations}
          selectedOrganization={selectedOrganization}
          onSelectOrganization={updateSelectedOrganization}
          onCreateOrganization={actions.openCreateOrganization}
          onOpenSettings={actions.openOrganizationSettings}
        />
        <Stack spacing={2} sx={{ px: { xs: 1, md: 2 } }}>
          <Stack
            direction={isCompactLandscape ? "row" : { xs: "column", md: "row" }}
            alignItems={isCompactLandscape ? "center" : { xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={isCompactLandscape ? 1 : 2}
            sx={{
              bgcolor: "background.paper",
              borderRadius: 2,
              px: { xs: 1, md: 1.5 },
              py: 1,
            }}
          >
            <Tabs
              value={unit}
              onChange={(event, newValue) => setUnit(newValue)}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                minHeight: 42,
                minWidth: 0,
                flex: 1,
                "& .MuiTabs-indicator": {
                  borderRadius: 999,
                },
                "& .MuiTab-root": {
                  minWidth: isCompactLandscape ? 56 : undefined,
                  px: isCompactLandscape ? 1 : undefined,
                  color: "text.secondary",
                  borderRadius: 1.5,
                  minHeight: 42,
                  "& .MuiSvgIcon-root": {
                    color: "inherit",
                  },
                  "&:hover": {
                    bgcolor: "action.hover",
                    color: "primary.main",
                  },
                  "&.Mui-selected": {
                    color: "primary.main",
                    bgcolor: "action.selected",
                  },
                },
              }}
            >
              <Tab
                id="tab-users"
                value={ORGANIZATION_UNITS.USERS}
                icon={<PeopleAltOutlined fontSize="small" />}
                iconPosition="start"
                label={renderTabLabel(getTranslation(translations, "users.name"), counts?.users_count)}
              />
              <Tab
                id="tab-groups"
                value={ORGANIZATION_UNITS.GROUPS}
                icon={<GroupsOutlined fontSize="small" />}
                iconPosition="start"
                label={renderTabLabel(getTranslation(translations, "groups.name"), counts?.groups_count)}
              />
              <Tab
                id="tab-roles"
                value={ORGANIZATION_UNITS.ROLES}
                icon={<ManageAccounts fontSize="small" />}
                iconPosition="start"
                label={renderTabLabel(getTranslation(translations, "roles.name"), counts?.roles_count)}
              />
              <Tab
                id="tab-robots"
                value={ORGANIZATION_UNITS.ROBOTS}
                icon={<PrecisionManufacturingOutlined fontSize="small" />}
                iconPosition="start"
                label={renderTabLabel(getTranslation(translations, "robots.name"), counts?.robots_count)}
              />
            </Tabs>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0, minWidth: 0 }}>
              <Box sx={{ width: isCompactLandscape ? 210 : { xs: "100%", sm: 320 } }}>
                <SearchBar
                  id="search-bar-panel"
                  setFilter={setSecondFilter}
                  placeholder={
                    isCompactLandscape
                      ? ""
                      : getTranslation(
                        translations,
                        "pm.organizationchart.ricerca.placeholder"
                      )
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "20px",
                      backgroundColor: "background.paper",
                    },
                  }}
                />
              </Box>
              <Tooltip title={isCompactLandscape ? primaryAction.label : ""}>
                <span>
                  <FlowentiButton
                    id={primaryAction.id}
                    disabled={!selectedOrganization?.id || !administrator}
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={primaryAction.onClick}
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: isCompactLandscape ? 40 : undefined,
                      px: isCompactLandscape ? 1.25 : undefined,
                      "& .MuiButton-startIcon": {
                        mr: isCompactLandscape ? 0 : undefined,
                        ml: isCompactLandscape ? 0 : undefined,
                      },
                    }}
                  >
                    {isCompactLandscape ? "" : primaryAction.label}
                  </FlowentiButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
        <Box sx={{ minHeight: 0, flex: 1, px: { xs: 1, md: 2 }, pb: { xs: 1, md: 2 } }}>
          <Paper
            variant="outlined"
            sx={{
              height: "100%",
              overflow: "hidden",
              border: { xs: "none", md: "1px solid" },
              borderColor: { md: "divider" },
              borderRadius: { xs: 0, md: 2 },
              bgcolor: "background.paper",
              boxShadow: "none",
            }}
          >
            {selectedOrganization?.id ? (
              <Box sx={{ height: "100%", p: { xs: 0, md: 2 } }}>
                <ChoosePanel
                  unit={unit}
                  setUserSelected={setUserSelected}
                  administrator={administrator}
                  organizationId={selectedOrganization.id}
                  onCreateGroup={actions.openCreateGroup}
                  onCreateRole={actions.openCreateRole}
                  onOpenRobotsShop={actions.openRobotsShop}
                  ref={(ref) => {
                    getUserUntilPageRef.current = ref?.getUsersUntilPage;
                    getInvitedUsers.current = ref?.getInvitedUsers;
                    getGroups.current = ref?.getGroups;
                    getUsers.current = ref?.getUsers;
                    getRoles.current = ref?.getRoles;
                    getRobots.current = ref?.getRobots;
                  }}
                />
              </Box>
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
                <Typography color="text.secondary">
                  {getTranslation(translations, "select_organization_info")}
                </Typography>
              </Stack>
            )}
          </Paper>
        </Box>
      </Stack>
    </React.Fragment>
  );
};

export default Organizations2;
